import React from "react";
import type { OnboardingStatus } from "~/lib/onboarding";
import { Stepper } from "~/components/ui/stepper";
import { Card, CardContent } from "./card";

export interface OnboardingDashboardProps {
  status: OnboardingStatus;
  children?: React.ReactNode;
}

export const OnboardingDashboard = ({
  status,
  children,
}: OnboardingDashboardProps) => {
  const size = 100;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - status.completionScore);
  const percent = Math.round(status.completionScore * 100);

  // Map section ids to routes
  const ROUTES: { [key: string]: string } = {
    personalInfo: "/coach/edit-profile/personal-info",
    profile: "/coach/edit-profile/profile",
    matchingDetails: "/coach/edit-profile/details",
    calendar: "/coach/edit-profile/client-flow",
    payments: "/coach/invoices",
  };

  const mandatorySteps = status.sections.map((sec) => ({
    href: ROUTES[sec.id],
    title: sec.name,
    description: sec.description,
    items: sec.items,
    completed: 1 <= sec.progress,
  }));

  return (
    <Card>
      <CardContent className="space-y-8">
        <div>
          <h2 className="font-heading text-text-primary text-2xl font-medium tracking-tight">
            Welcome to Dream Coach Match!
          </h2>
          <p className="text-text-tertiary text-sm">
            Follow these steps to complete your profile, get discovered on the
            platform and start growing your coaching practice.
          </p>
        </div>
        <div className="flex flex-col items-start gap-6 sm:flex-row">
          <div
            className="relative flex items-center justify-center"
            style={{ width: `${size}px`, height: `${size}px` }}
          >
            <svg
              className="absolute h-full w-full -rotate-90"
              viewBox={`0 0 ${size} ${size}`}
            >
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                strokeWidth={strokeWidth}
                className="stroke-muted"
                fill="none"
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                strokeWidth={strokeWidth}
                className="stroke-brand-700 transition-all"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute flex flex-col items-center justify-center text-center">
              <span className="font-heading text-2xl font-medium tracking-tighter">
                {percent}%
              </span>
            </div>
          </div>
          <Stepper steps={mandatorySteps} />
        </div>
        {children}
      </CardContent>
    </Card>
  );
};
